// src/components/study/NewVersionDialog.js
//
// "Is this a new version of the protocol?" Asked before EVERY protocol upload
// on a study that already has one (study page and SourceX), with the version
// numbers named, so versions stay easy to follow. Its own classes
// (protocol-version-*) so its colours don't depend on another pop-up's.

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { GitBranch } from "lucide-react";

/**
 * @param {{open:boolean, fileName?:string, current:number, next:number, isAdmin:boolean,
 *          existing?:string, onYes:()=>void, onNo:()=>void, onCancel:()=>void}} props
 *   existing: the current protocol described in full ("Version 1 · x.pdf, uploaded by … in SourceX on …").
 */
const NewVersionDialog = ({ open, fileName, current, next, isAdmin, existing, onYes, onNo, onCancel }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Is this a new version of the protocol?"
        className="protocol-version-dialog w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0826]/95 p-5 text-slate-100 shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)]"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="protocol-version-icon w-9 h-9 rounded-xl bg-purple-500/15 border border-white/15 flex items-center justify-center shrink-0">
            <GitBranch className="w-4 h-4 text-purple-300" />
          </div>
          <h2 className="text-base font-semibold text-white">Is this a new version of the protocol?</h2>
        </div>
        {fileName && <p className="text-[12px] text-slate-400 mb-2 break-words">{fileName}</p>}
        <p className="text-sm text-slate-300">
          The current protocol is {existing || `Version ${current}`}.{!isAdmin && " If this is a new version, an admin will be asked to approve it."}
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button type="button" onClick={onCancel} className="protocol-version-soft-btn px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-200">
            Cancel
          </button>
          <button type="button" onClick={onNo} className="protocol-version-soft-btn px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-200">
            No, same as Version {current}
          </button>
          <button
            type="button"
            onClick={onYes}
            className="protocol-version-solid-btn px-3 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-sm font-semibold text-white ring-1 ring-white/20"
          >
            Yes, Version {next}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NewVersionDialog;
